<template>
  <div class="ldap-login-form">
    <CAlert color="danger" v-if="error" class="mb-3">{{ error }}</CAlert>

    <CInput
      ref="username"
      :placeholder="$pgettext('ldap.placeholder','Username')"
      v-model="v$.username.$model"
      :label="$pgettext('ldap.label','Username')"
      :is-valid="!v$.username.$error"
      autocomplete="username"
      @keyup.enter="login">
      <template v-slot:invalid-feedback>
        <div class="invalid-feedback d-block" v-for="error of v$.username.$errors" :key="error.$uid">
          <div class="error-msg">{{ error.$message }}</div>
        </div>
      </template>
    </CInput>

    <InputPassword
      :placeholder="$pgettext('ldap.placeholder','Password')"
      v-model="v$.password.$model"
      :is-valid="!v$.password.$error"
      :label="$pgettext('ldap.label','Password')"
      @keyup-enter="login">
      <template v-slot:invalid-feedback>
        <div class="invalid-feedback d-block" v-for="error of v$.password.$errors" :key="error.$uid">
          <div class="error-msg">{{ error.$message }}</div>
        </div>
      </template>
    </InputPassword>

    <PButton :loading="loading" type="button" color="primary" block @click="login">
      <translate translate-context="ldap.button">Login with company credentials</translate>
    </PButton>

    <!-- Alternative login methods -->
    <div v-if="alternativeProviders.length > 0" class="text-center mt-3">
      <CLink v-for="provider in alternativeProviders" :key="provider"
             @click="$emit('switch-provider', provider)"
             class="text-muted small d-block mb-1">
        <translate translate-context="login.alternative">{{ getProviderLabel(provider) }}</translate>
      </CLink>
    </div>
  </div>
</template>

<script>
import InputPassword from "@/components/InputPassword.vue";
import PButton from "@/components/PButton.vue";
import {useVuelidate} from "@vuelidate/core";
import {required$} from "@/domain/core/utils/validators";
import {AuthProviders} from "@/domain/login/constant/authProviders";
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'LdapLoginForm',
  components: {PButton, InputPassword},
  setup() {
    return {v$: useVuelidate()}
  },
  data() {
    return {
      username: null,
      password: null,
      submitted: false,
      loading: false,
      error: null
    }
  },
  validations() {
    return {
      username: {required$},
      password: {required$}
    }
  },
  computed: {
    ...mapGetters('login', ['authProviderFactory']),
    isValid() {
      return !this.v$.$invalid
    },
    alternativeProviders() {
      const provider = this.authProviderFactory.getProvider(AuthProviders.LDAP)
      return provider.getAlternatives((p) => this.authProviderFactory.isEnabled(p))
    }
  },
  mounted() {
    if (this.$refs.username) {
      this.$refs.username.$el.children[0].focus()
    }
  },
  methods: {
    ...mapActions('login', ['authenticate']),

    async login() {
      if (!await this.v$.$validate()) return

      this.submitted = true
      if (!this.isValid) return

      this.error = null

      try {
        this.loading = true

        await this.authenticate({
          method: AuthProviders.LDAP,
          credentials: {
            username: this.username,
            password: this.password
          },
          remember: true
        })

        this.$emit('login-success')
      } catch (e) {
        // Handle LDAP-specific errors
        if (e.message?.code === 'auth.ldap.invalid_credentials') {
          this.error = this.$pgettext('login.error', 'Invalid username or password')
        } else if (e.message?.code === 'auth.ldap.user_creation_failed') {
          this.error = this.$pgettext('login.error', 'Failed to create user account')
        } else if (e.message?.code === 'auth.ldap.internal_error') {
          this.error = this.$pgettext('login.error', 'Internal server error')
        } else {
          this.error = this.$pgettext('login.error', 'Login error')
        }
      } finally {
        this.loading = false
      }
    },

    getProviderLabel(provider) {
      switch (provider) {
        case AuthProviders.PASSWORD:
          return this.$pgettext('login.alternative', 'Use email and password')
        case AuthProviders.EMAIL_TOKEN:
          return this.$pgettext('login.alternative', 'Use email and token')
        case AuthProviders.GOOGLE:
          return this.$pgettext('login.alternative', 'Use Google')
        case AuthProviders.FACEBOOK:
          return this.$pgettext('login.alternative', 'Use Facebook')
        case AuthProviders.APPLE:
          return this.$pgettext('login.alternative', 'Use Apple')
        default:
          return provider
      }
    }
  }
}
</script>

<style scoped>
.ldap-login-form {
  width: 100%;
}
</style>
